import { z } from "zod";
import { LIMITS } from "./limits";

/** Lower-case words joined by single hyphens: what a post is addressed by in its URL. */
export const slugSchema = z
  .string()
  .min(LIMITS.slug.min)
  .max(LIMITS.slug.max)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lower-case letters, numbers and single hyphens.");

export const postStatusSchema = z.enum(["draft", "published"]);

const title = z.string().trim().min(LIMITS.title.min).max(LIMITS.title.max);
const summary = z.string().trim().min(LIMITS.summary.min).max(LIMITS.summary.max);
const bodyMd = z.string().min(LIMITS.bodyMd.min).max(LIMITS.bodyMd.max);

/** What the blog list shows. It never carries the body. */
export const postSummarySchema = z.object({
  slug: slugSchema,
  title,
  summary,
  status: postStatusSchema,
  publishedAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
});

export const postSchema = postSummarySchema.extend({
  bodyMd,
  createdAt: z.iso.datetime(),
});

/** Orla creates a post. Without a slug one is made from the title. */
export const postCreateSchema = z.strictObject({
  slug: slugSchema.optional(),
  title,
  summary,
  bodyMd,
  status: postStatusSchema.default("draft"),
});

/** Orla edits a post. Any subset of fields, at least one. */
export const postPatchSchema = z
  .strictObject({
    title: title.optional(),
    summary: summary.optional(),
    bodyMd: bodyMd.optional(),
    status: postStatusSchema.optional(),
  })
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: "Give at least one field to change.",
  });

export type PostStatus = z.infer<typeof postStatusSchema>;
export type PostSummary = z.infer<typeof postSummarySchema>;
export type Post = z.infer<typeof postSchema>;
export type PostCreate = z.infer<typeof postCreateSchema>;
export type PostPatch = z.infer<typeof postPatchSchema>;
